<template>
    <div>
        <custom-navbar v-model="activeTab" center-collapsed not-line>
            <template #left>
                <custom-navbar-item id="home">
                    <router-link
                        to="/app/tenants"
                        class="rounded-full bg-brand-blue flex justify-center items-center p-2"
                    >
                        <span>🏠</span>
                    </router-link>
                </custom-navbar-item>
                <custom-navbar-item
                    id="general"
                    :active="['general', 'home'].includes(activeTab)"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden">General&nbsp;</span>
                        <span>⚙️</span>
                    </div>
                </custom-navbar-item>
                <custom-navbar-item
                    id="owner"
                    :active="activeTab === 'owner'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Propietario&nbsp; </span>
                        <span>👤</span>
                    </div>
                </custom-navbar-item>
                <custom-navbar-item
                    v-if="$ability.hasPermission('tenant:GetSubscription')"
                    id="subscription"
                    :active="activeTab === 'subscription'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Plan&nbsp; </span>
                        <span>💳</span>
                    </div>
                </custom-navbar-item>
                <custom-navbar-item
                    v-if="$ability.hasPermission('tenant:GetSubscription')"
                    id="invoices"
                    :active="activeTab === 'invoices'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Facturas&nbsp; </span>
                        <span>🧾</span>
                    </div>
                </custom-navbar-item>
            </template>
        </custom-navbar>

        <div v-show="['general', 'home'].includes(activeTab)" class="p-4">
            <div class="w-full flex flex-col gap-4">
                <div>
                    <span
                        class="block mb-1 text-sm text-theme-muted font-semibold"
                        >ID</span
                    >
                    <uuid-display :value="itemComponent.id" full />
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <custom-input
                        :value="itemComponent.name"
                        label="Nombre"
                        disabled
                    />
                    <custom-input
                        :value="itemComponent.slug"
                        label="Slug"
                        disabled
                    />
                </div>
                <custom-input
                    :value="itemComponent.createdAt | date"
                    label="Creado"
                    disabled
                />
                <div
                    v-if="canViewCatalog || canSelectTenant"
                    class="flex justify-center gap-3"
                >
                    <app-button
                        v-if="canViewCatalog"
                        border
                        @click="viewCatalog"
                    >
                        <span class="flex items-center gap-2">
                            <link-icon class="w-4 h-4" />
                            Ver catálogo
                        </span>
                    </app-button>
                    <app-button
                        v-if="canSelectTenant"
                        color="brand-blue"
                        @click="selectTenant"
                    >
                        Seleccionar tenant
                    </app-button>
                </div>
            </div>
        </div>

        <div v-show="activeTab === 'owner'" class="p-4">
            <div class="w-full flex flex-col gap-4">
                <template v-if="itemComponent.owner">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <custom-input
                            :value="`${itemComponent.owner.firstName} ${itemComponent.owner.lastName}`"
                            label="Nombre"
                            disabled
                        />
                        <custom-input
                            :value="itemComponent.owner.email"
                            label="Correo"
                            disabled
                        />
                    </div>
                    <div>
                        <span
                            class="block mb-1 text-sm text-theme-muted font-semibold"
                            >ID</span
                        >
                        <uuid-display :value="itemComponent.owner.id" full />
                    </div>
                    <div
                        v-if="$ability.hasPermission('user:Get', true)"
                        class="flex justify-center"
                    >
                        <router-link
                            :to="`/app/users/detail/${itemComponent.owner.id}`"
                            class="text-theme-primary"
                        >
                            Ver usuario
                        </router-link>
                    </div>
                </template>
                <p v-else class="text-sm text-theme-muted text-center">
                    Este tenant no tiene un owner asignado.
                </p>
            </div>
        </div>

        <div
            v-if="$ability.hasPermission('tenant:GetSubscription')"
            v-show="activeTab === 'subscription'"
            class="p-4"
        >
            <div class="w-full flex flex-col gap-6">
                <p
                    v-if="subscriptionLoaded && !current"
                    class="text-xl font-semibold text-theme-base"
                >
                    Este tenant todavía no tiene un plan.
                </p>

                <platform-subscription-card
                    v-if="current"
                    :current="current"
                    :scheduled="scheduled"
                    :pending-change="pendingChange"
                    :extensions="currentExtensions"
                />

                <div class="flex flex-wrap gap-3">
                    <app-button
                        v-if="canAssignPlan"
                        border
                        @click="openAssignDialog"
                    >
                        Asignar plan
                    </app-button>
                    <app-button
                        v-if="canExtendPeriod"
                        border
                        @click="showExtendPeriodDialog = true"
                    >
                        Extender periodo
                    </app-button>
                </div>

                <div v-if="history.length" class="w-full flex flex-col gap-2">
                    <h3 class="text-base font-semibold text-theme-base">
                        Historial
                    </h3>
                    <custom-table
                        :fields="$data.$historyFields"
                        :items="history"
                        outlined
                        items-empty-message="Sin historial."
                        class="w-full"
                    >
                        <template #cell(type)="data">
                            {{ typeLabel(data.item.type) }}
                        </template>
                        <template #cell(status)="data">
                            <subscription-status-badge
                                :status="data.item.status"
                            />
                        </template>
                        <template #cell(source)="data">
                            {{ sourceLabel(data.item.source) }}
                        </template>
                        <template #cell(createdByUserId)="data">
                            <uuid-display
                                v-if="data.item.createdByUserId"
                                :value="data.item.createdByUserId"
                            />
                            <span v-else class="text-theme-muted">—</span>
                        </template>
                        <template #cell(period)="data">
                            {{ data.item.currentPeriodStart | date }} →
                            <template v-if="data.item.currentPeriodEnd">
                                {{ data.item.currentPeriodEnd | dateSimple }}
                            </template>
                            <span v-else class="text-theme-muted">
                                sin corte
                            </span>
                        </template>
                        <template #cell(extensions)="data">
                            <span
                                v-for="(extension, index) in data.item
                                    .extensions"
                                :key="index"
                                class="block"
                            >
                                +{{ extension.days }} días ·
                                {{ extension.at | date }}
                            </span>
                            <span
                                v-if="!data.item.extensions.length"
                                class="text-theme-muted"
                            >
                                —
                            </span>
                        </template>
                    </custom-table>
                </div>
            </div>
        </div>

        <div
            v-if="
                $ability.hasPermission('tenant:GetSubscription') &&
                activeTab === 'invoices' &&
                invoicesLoaded
            "
            class="p-4"
        >
            <platform-invoices-table :items="invoices" />
        </div>

        <platform-plan-selector-dialog
            v-model="showAssignDialog"
            title="Asignar plan"
            confirm-label="Asignar"
            :options="assignOptions"
            :loading="isAssigning"
            @confirm="assignPlan"
        />

        <tenant-subscription-extend-period-dialog
            v-model="showExtendPeriodDialog"
            :loading="isExtendingPeriod"
            @confirm="extendPeriod"
        />
    </div>
</template>

<script>
import tabQuerySyncMixin from "@/mixins/tabQuerySyncMixin";
import { mapActions, mapGetters, mapState } from "vuex";

import LinkIcon from "@/components/icons/LinkIcon.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import CustomTable from "@/components/utils/CustomTable.vue";
import PlatformInvoicesTable from "@/components/utils/PlatformInvoicesTable.vue";
import PlatformPlanSelectorDialog from "@/components/utils/PlatformPlanSelectorDialog.vue";
import PlatformSubscriptionCard from "@/components/utils/PlatformSubscriptionCard.vue";
import SubscriptionStatusBadge from "@/components/utils/SubscriptionStatusBadge.vue";
import UuidDisplay from "@/components/utils/UuidDisplay.vue";

import TenantSubscriptionExtendPeriodDialog from "./TenantSubscriptionExtendPeriodDialog.vue";

const FREE_PLAN_CODE = "free";

const TYPE_LABELS = {
    trial: "Prueba",
    paid: "Pago",
    free: "Free"
};

const SOURCE_LABELS = {
    onboarding: "Onboarding",
    my_plan: "Mi plan",
    super: "Super",
    system: "Sistema"
};

export default {
    name: "TenantDetail",
    components: {
        LinkIcon,
        CustomInput,
        CustomTable,
        UuidDisplay,
        SubscriptionStatusBadge,
        PlatformInvoicesTable,
        PlatformPlanSelectorDialog,
        PlatformSubscriptionCard,
        TenantSubscriptionExtendPeriodDialog
    },
    mixins: [tabQuerySyncMixin],
    data: () => ({
        activeTab: "general",
        $tabsWhitelist: ["general", "owner", "subscription", "invoices"],
        $historyFields: [
            { key: "planSlug", label: "Plan", sortable: false },
            { key: "type", label: "Tipo", sortable: false },
            { key: "status", label: "Estado", sortable: false },
            { key: "source", label: "Origen", sortable: false },
            { key: "createdByUserId", label: "Creado por", sortable: false },
            { key: "period", label: "Inicio → corte", sortable: false },
            { key: "extensions", label: "Extensiones", sortable: false }
        ],
        itemComponent: {
            id: "",
            name: "",
            slug: "",
            createdAt: "",
            owner: null
        },
        subscriptionState: null,
        subscriptionLoaded: false,
        invoices: [],
        invoicesLoaded: false,
        assignOptions: [],
        showAssignDialog: false,
        showExtendPeriodDialog: false,
        isAssigning: false,
        isExtendingPeriod: false
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        ...mapState("auth", ["tenantId"]),
        canSelectTenant() {
            return (
                !this.tenantId && this.$ability.hasPermission("tenant:Select")
            );
        },
        canViewCatalog() {
            return (
                this.$isMultitenant &&
                !!this.$franchiseDomain &&
                !!this.itemComponent.slug
            );
        },
        current() {
            return this.subscriptionState
                ? this.subscriptionState.current
                : null;
        },
        scheduled() {
            return this.subscriptionState
                ? this.subscriptionState.scheduled
                : null;
        },
        pendingChange() {
            return this.subscriptionState
                ? this.subscriptionState.pendingChange
                : null;
        },
        history() {
            return this.subscriptionState
                ? this.subscriptionState.history || []
                : [];
        },
        currentExtensions() {
            if (!this.current) return [];
            const currentRow = this.history.find(
                (item) => item.id === this.current.id
            );
            return currentRow ? currentRow.extensions : [];
        },
        // El super solo asigna Free o pruebas, y solo si el tenant no tiene
        // plan o está en Free (un plan pago depende de una factura pagada).
        canAssignPlan() {
            return (
                this.subscriptionLoaded &&
                this.$ability.hasPermission("platformSubscription:Assign") &&
                (!this.current || this.current.type === "free")
            );
        },
        canExtendPeriod() {
            return (
                this.$ability.hasPermission(
                    "platformSubscription:ExtendPeriod"
                ) && Boolean(this.current && this.current.currentPeriodEnd)
            );
        }
    },
    watch: {
        "$route.params.id"() {
            this.initComponent();
        },
        // Plan y facturas se piden solo al abrir su tab.
        activeTab() {
            this.loadActiveTabData();
        }
    },
    async mounted() {
        await this.initComponent();
    },
    methods: {
        ...mapActions("tenants", ["getTenant"]),
        ...mapActions("auth", ["applyTenantContext"]),
        ...mapActions("platformSubscriptions", [
            "getTenantSubscription",
            "getTenantPlans",
            "assignTenantSubscription",
            "extendTenantSubscriptionPeriod",
            "listTenantPlatformInvoices"
        ]),
        typeLabel(type) {
            return TYPE_LABELS[type] || type;
        },
        sourceLabel(source) {
            return SOURCE_LABELS[source] || source;
        },
        async showError(error) {
            this.$sound.play("error");
            await this.$swal.fire({
                background: this.backgroundColor,
                title: error.title,
                text: error.message,
                icon: error.icon
            });
        },
        viewCatalog() {
            window.open(
                `https://${this.itemComponent.slug}.${this.$franchiseDomain}`,
                "_blank"
            );
        },
        async selectTenant() {
            const result = await this.$swal.fire({
                background: this.backgroundColor,
                title: `¿Seleccionar "${this.itemComponent.name}"?`,
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Seleccionar",
                cancelButtonText: "Cancelar"
            });
            if (!result.isConfirmed) return;
            this.$loading.show();
            try {
                await this.applyTenantContext({
                    tenantId: this.itemComponent.id,
                    tenantSlug: this.itemComponent.slug
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: `Tenant "${this.itemComponent.name}" seleccionado`,
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$router.push("/app");
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                await this.showError(error);
            }
        },
        async initComponent() {
            const verifyPagePermission =
                this.$ability.verifyPagePermission.bind(this);
            if (!(await verifyPagePermission("tenant:List", "/app/tenants")))
                return;
            const { id } = this.$route.params;
            this.subscriptionLoaded = false;
            this.invoicesLoaded = false;
            await this.loadItem(id);
            await this.loadActiveTabData();
        },
        async loadActiveTabData() {
            if (
                !this.itemComponent.id ||
                !this.$ability.hasPermission("tenant:GetSubscription")
            ) {
                return;
            }
            if (this.activeTab === "subscription" && !this.subscriptionLoaded) {
                await this.loadSubscription();
            }
            if (this.activeTab === "invoices" && !this.invoicesLoaded) {
                await this.loadInvoices();
            }
        },
        async loadItem(id) {
            this.$loading.show();
            try {
                this.itemComponent = await this.getTenant({ id });
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                await this.showError(error);
                if (error.statusCode === 404) this.$router.push("/app/tenants");
            }
        },
        async loadInvoices() {
            try {
                const { items } = await this.$loading.wrap(() =>
                    this.listTenantPlatformInvoices({
                        tenantId: this.itemComponent.id
                    })
                );
                this.invoices = items;
                this.invoicesLoaded = true;
            } catch (error) {
                await this.showError(error);
            }
        },
        async loadSubscription() {
            this.$loading.show();
            try {
                this.subscriptionState = await this.getTenantSubscription({
                    tenantId: this.itemComponent.id
                });
                this.subscriptionLoaded = true;
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                await this.showError(error);
            }
        },
        // Opciones: Free y una prueba por cada plan que la tenga.
        async openAssignDialog() {
            try {
                const { items } = await this.$loading.wrap(() =>
                    this.getTenantPlans({ tenantId: this.itemComponent.id })
                );
                const options = [];
                items.forEach((plan) => {
                    if (plan.code === FREE_PLAN_CODE) {
                        options.push({
                            value: `${plan.slug}:free`,
                            planSlug: plan.slug,
                            planCode: plan.code,
                            label: plan.name,
                            hint: "Sin costo, sin fecha de corte"
                        });
                        return;
                    }
                    if (!plan.trialAvailable) return;
                    options.push({
                        value: `${plan.slug}:trial`,
                        planSlug: plan.slug,
                        planCode: plan.code,
                        mode: "trial",
                        label: `${plan.name} · prueba de ${plan.trialDays} días`,
                        hint: "Sin cobro durante la prueba",
                        warning: plan.trialUsedByTenant
                            ? "Este tenant ya usó esta prueba. Se le asignará de nuevo y quedará registrado."
                            : ""
                    });
                });
                this.assignOptions = options;
                this.showAssignDialog = true;
            } catch (error) {
                await this.showError(error);
            }
        },
        async assignPlan(option) {
            this.isAssigning = true;
            try {
                this.subscriptionState = await this.assignTenantSubscription({
                    tenantId: this.itemComponent.id,
                    planSlug: option.planSlug,
                    mode: option.mode
                });
                this.isAssigning = false;
                this.showAssignDialog = false;
                this.invoicesLoaded = false;
                this.$sound.play();
            } catch (error) {
                this.isAssigning = false;
                await this.showError(error);
            }
        },
        async extendPeriod({ days }) {
            this.isExtendingPeriod = true;
            try {
                await this.extendTenantSubscriptionPeriod({
                    subscriptionId: this.current.id,
                    days
                });
                this.isExtendingPeriod = false;
                this.showExtendPeriodDialog = false;
                this.invoicesLoaded = false;
                this.$sound.play();
                await this.loadSubscription();
            } catch (error) {
                this.isExtendingPeriod = false;
                await this.showError(error);
            }
        }
    }
};
</script>
